'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, LoaderCircle, Pencil, X } from 'lucide-react';

import { cn } from '../lib/cn';

type WorktreeAliasEditorProps = {
  worktreeId: string;
  alias: string | null;
  /** 저장 실패는 reject로 전달해야 편집 상태와 입력값을 보존할 수 있습니다. */
  onSave: (worktreeId: string, alias: string) => Promise<void>;
  editLabel: string;
  placeholder: string;
  saveLabel: string;
  cancelLabel: string;
  fallbackName?: string;
  fallbackMonospace?: boolean;
  isSaving?: boolean;
  readOnly?: boolean;
  variant?: 'list' | 'detail';
};

export function WorktreeAliasEditor({
  worktreeId,
  alias,
  onSave,
  editLabel,
  placeholder,
  saveLabel,
  cancelLabel,
  fallbackName,
  fallbackMonospace = true,
  isSaving = false,
  readOnly = false,
  variant = 'list',
}: WorktreeAliasEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [value, setValue] = useState(alias ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);
  const sourceRef = useRef({ worktreeId, alias });

  useEffect(() => {
    const previous = sourceRef.current;
    const worktreeChanged = previous.worktreeId !== worktreeId;
    const aliasChanged = previous.alias !== alias;
    sourceRef.current = { worktreeId, alias };

    if (worktreeChanged) {
      setValue(alias ?? '');
      setIsEditing(false);
      return;
    }

    if (aliasChanged && !isEditing) {
      setValue(alias ?? '');
    }
  }, [alias, isEditing, worktreeId]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const handleSave = useCallback(async () => {
    if (isSubmittingRef.current) return;

    const trimmed = value.trim();
    if (trimmed === (alias ?? '')) {
      setIsEditing(false);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      await onSave(worktreeId, trimmed);
      setValue(trimmed);
      setIsEditing(false);
    } catch {
      // 호출부가 사용자용 오류 메시지를 표시합니다. 입력은 보존해 즉시 재시도할 수 있게 합니다.
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [alias, onSave, value, worktreeId]);

  const resolvedFallbackName = fallbackName ?? `wt-${worktreeId}`;
  const displayName = value.trim() || resolvedFallbackName;
  const isBusy = isSaving || isSubmitting;

  const handleCancel = useCallback(() => {
    if (isBusy) return;
    setValue(alias ?? '');
    setIsEditing(false);
  }, [alias, isBusy]);

  if (isEditing) {
    return (
      <div
        className={cn(
          'inline-flex min-w-0 items-center overflow-hidden rounded-md bg-base-200/70 ring-1 ring-inset ring-base-content/10 transition-colors focus-within:bg-base-100 focus-within:ring-primary/40',
          variant === 'detail' ? 'h-7 w-52 max-w-[48vw]' : 'h-8 flex-1',
        )}
        aria-busy={isBusy}
      >
        <input
          ref={inputRef}
          type="text"
          className={cn(
            'h-full min-w-0 flex-1 bg-transparent px-2 font-medium text-base-content outline-none placeholder:text-[var(--color-fg-subtle)] disabled:cursor-wait disabled:opacity-60',
            variant === 'detail' ? 'text-xs' : 'text-sm',
          )}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void handleSave();
            }
            if (event.key === 'Escape') {
              handleCancel();
            }
          }}
          maxLength={100}
          placeholder={placeholder}
          aria-label={editLabel}
          disabled={isBusy}
        />
        <div className="flex h-full shrink-0 items-center border-l border-base-content/10 px-0.5">
          <button
            type="button"
            className="inline-flex h-6 w-6 items-center justify-center rounded text-[var(--color-fg-muted)] transition-colors hover:bg-primary/10 hover:text-primary disabled:cursor-wait disabled:opacity-50"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => void handleSave()}
            title={saveLabel}
            aria-label={saveLabel}
            disabled={isBusy}
          >
            {isBusy ? (
              <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            className="inline-flex h-6 w-6 items-center justify-center rounded text-[var(--color-fg-muted)] transition-colors hover:bg-base-300 hover:text-base-content disabled:cursor-not-allowed disabled:opacity-40"
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleCancel}
            title={cancelLabel}
            aria-label={cancelLabel}
            disabled={isBusy}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  if (readOnly) {
    return (
      <span
        className={cn(
          'min-w-0 truncate text-left font-medium',
          variant === 'detail'
            ? 'text-xs text-[var(--color-fg-subtle)]'
            : 'flex-1 text-sm text-[var(--color-fg-subtle)]',
        )}
        title={displayName}
      >
        {value.trim() || !fallbackMonospace ? displayName : <span className="font-mono">{resolvedFallbackName}</span>}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={cn(
        'group inline-flex min-w-0 items-center gap-1.5 border-b border-dashed border-base-content/20 text-left font-medium transition-colors hover:border-primary/40 hover:text-primary disabled:cursor-wait disabled:opacity-60',
        variant === 'detail' ? 'max-w-64 px-0.5 py-0.5 text-xs text-[var(--color-fg-subtle)]' : 'flex-1 text-sm',
      )}
      onClick={() => {
        setValue(alias ?? '');
        setIsEditing(true);
      }}
      title={displayName}
      aria-label={`${editLabel}: ${displayName}`}
      disabled={isBusy}
    >
      <span
        className={cn(
          'min-w-0 truncate',
          !value.trim() && fallbackMonospace && 'font-mono text-[var(--color-fg-subtle)]',
        )}
      >
        {displayName}
      </span>
      <Pencil className="h-3 w-3 shrink-0 opacity-45 transition-opacity group-hover:opacity-100" aria-hidden="true" />
    </button>
  );
}
